import * as PIXI from "pixi.js";
import { NoteUITrack, SoundFileUITrack } from "../Shared/UITrack.js";
import { NoteGroupTimelineEvent, OneShotTimelineEvent } from "../Shared/TrackTimelineEvent.js";
import { UIColors } from "../Settings/UITheme.js";
import { ScrollableTimeline } from "../Shared/ScrollableTimeline.js";
import { TimelineMode, MouseClickType } from "../Settings/Enums.js";
import { editType } from "../Settings/EditType.js";
import { UITrackStore } from "../ReactUI/UITrackStore.js";
import { mouseState } from "../Shared/MouseState.js";
export class SongTimeline extends ScrollableTimeline {
    constructor(startX, endX, endY, songManager, showSequencerCallback) {
        super(startX, endX, 0, endY, songManager);
        this.songManager = songManager;
        this._noteGroupTimelineEvents = [];
        this._showSequencerCallback = showSequencerCallback;
        this._newEventGraphics = new PIXI.Graphics();
        this.addChildAt(this._newEventGraphics, this.children.length - 3);
        this._regenerateTimeline(0);
        this._UITrackUpdateHandler = this._UITrackUpdateHandler.bind(this);
        this._cleanupListener = UITrackStore.subscribe(this._UITrackUpdateHandler);
    }
    get contentHeight() {
        let tracks = UITrackStore.getState().tracks;
        if (tracks.length == 0) {
            return 0;
        }
        else {
            return tracks[tracks.length - 1].startY + tracks[tracks.length - 1].height;
        }
    }
    destroy() {
        this._cleanupListener();
        super.destroy();
    }
    pointerMoveHandler(event) {
        let tracks = UITrackStore.getState().tracks;
        this._newEventGraphics.visible = false;
        super.pointerMoveHandler(event);
        if (this.timelineMode == TimelineMode.Edit && mouseState.buttons === 0) {
            this._newEventData = undefined;
            let mousePos = event.data.getLocalPosition(this.parent);
            mousePos.y -= this._newEventGraphics.y;
            for (let i = 0; i < tracks.length; i++) {
                if (tracks[i].startY + this._verticalScrollPosition < mousePos.y && tracks[i].startY + tracks[i].height + this._verticalScrollPosition > mousePos.y) {
                    let track = tracks[i];
                    if (mousePos.x < this.startX || mousePos.x > this.endX) {
                        return;
                    }
                    let [barPosition, beatPosition, numBeats] = this._getBarFromStageCoordinates(mousePos.x);
                    beatPosition = this.snapBeatToDragType(beatPosition);
                    barPosition += beatPosition / numBeats;
                    let startPosition = this.metadata.positionBarsToQuarterNote(barPosition);
                    let endPosition = startPosition;
                    let x = this._getStageCoordinatesFromBar(barPosition);
                    let y = track.startY + this._verticalScrollPosition;
                    let width = 0;
                    let height = track.height;
                    if (track instanceof NoteUITrack) {
                        endPosition = startPosition + 4;
                        if (track.getNoteGroupsWithinTime(startPosition, endPosition).length > 0) {
                            return;
                        }
                    }
                    else if (track instanceof SoundFileUITrack) {
                        endPosition = startPosition + track.eventDuration;
                        let existingEvents = track.getOneShotsBetweenTime(startPosition, endPosition);
                        if (!track.track.allowOverlaps && existingEvents.length > 0) {
                            return;
                        }
                        else {
                            if (existingEvents.length == 1 && existingEvents[0].startPosition == startPosition && existingEvents[0].endPosition == endPosition) {
                                return;
                            }
                        }
                    }
                    width = (this.metadata.positionQuarterNoteToBeats(endPosition) - this.metadata.positionQuarterNoteToBeats(startPosition)) * this.beatWidth;
                    width = Math.max(width, 5);
                    this._newEventGraphics.clear();
                    this._newEventGraphics.beginFill(UIColors.trackEventColor)
                        .drawRect(x, y + 2, width, height - 2)
                        .endFill()
                        .beginHole()
                        .drawRect(x + 2, y + 4, width - 4, height - 6)
                        .endHole();
                    this._newEventGraphics.visible = true;
                    this._newEventData = { track: track, startPosition: startPosition };
                    break;
                }
            }
        }
        else {
            this._newEventGraphics.visible = false;
        }
    }
    pointerUpClickHandler(event) {
        super.pointerUpClickHandler(event);
        if (this._mouseClickType == MouseClickType.LeftClick && this.timelineMode == TimelineMode.Edit && this._newEventData != undefined) {
            let track = this._newEventData.track;
            let startPosition = this._newEventData.startPosition;
            if (track instanceof NoteUITrack) {
                track.addNoteGroup(startPosition, startPosition + 4);
                this._initialiseNoteGroup([startPosition, startPosition + 4], track);
            }
            else if (track instanceof SoundFileUITrack) {
                let event = track.track.addOneShot(startPosition);
                this._initialiseOneShotTimelineEvent(event, track);
            }
            this._newEventData = undefined;
        }
    }
    mouseWheelHandler(event, canvasX, canvasY) {
        this._newEventGraphics.visible = false;
        super.mouseWheelHandler(event, canvasX, canvasY);
    }
    regenerateTracks() {
        while (this._eventContainer.children[0]) {
            let timelineEvent = this._eventContainer.children[0];
            timelineEvent.destroy();
        }
        this._noteGroupTimelineEvents = [];
        this._initialiseTrackTimelineEvents();
    }
    reinitialiseTrack(track) {
        for (let i = 0; i < this._eventContainer.children.length; i++) {
            let timelineEvent = this._eventContainer.children[i];
            if (timelineEvent.track === track) {
                timelineEvent.reinitialise();
            }
        }
    }
    regenerateNoteGroups() {
        this._noteGroupTimelineEvents.forEach(timelineEvent => {
            timelineEvent.destroy();
        });
        this._noteGroupTimelineEvents = [];
        UITrackStore.getState().tracks.forEach(track => {
            if (track instanceof NoteUITrack) {
                track.noteGroups.forEach(noteGroup => {
                    this._initialiseNoteGroup(noteGroup, track);
                });
            }
        });
    }
    addedHandler() {
        this.regenerateNoteGroups();
        editType.noteLengthDisabled = true;
        super.addedHandler();
    }
    _initialiseTrackTimelineEvents() {
        let tracks = UITrackStore.getState().tracks;
        for (let i = 0; i < tracks.length; i++) {
            let track = tracks[i];
            if (track instanceof NoteUITrack) {
                track.noteGroups.forEach(group => {
                    this._initialiseNoteGroup(group, track);
                });
            }
            else if (track instanceof SoundFileUITrack) {
                track.track.timeline.events.forEach(event => {
                    this._initialiseOneShotTimelineEvent(event, track);
                });
            }
        }
        ;
    }
    _initialiseNoteGroup(noteGroup, track) {
        let event = new NoteGroupTimelineEvent(this, track, noteGroup, this._showSequencerCallback);
        this._eventContainer.addChild(event);
        this._noteGroupTimelineEvents.push(event);
        return event;
    }
    _initialiseOneShotTimelineEvent(event, track) {
        let timelineEvent = new OneShotTimelineEvent(this, track, event);
        this._eventContainer.addChild(timelineEvent);
        return timelineEvent;
    }
    _UITrackUpdateHandler() {
        let barHeight = Math.max(this.contentHeight, this.endY);
        for (let i = 0; i < this._barContainer.children.length; i++) {
            let bar = this._barContainer.children[i];
            bar.resize(barHeight);
        }
        this.regenerateTracks();
    }
}
//# sourceMappingURL=SongTimeline.js.map